import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/features/auth/api/auth.api'
import { isTwoFactorChallenge, type AuthTokenResponse } from '@/features/auth/types'
import type { User } from '@/shared/types'
import { bareHttp } from '@/core/api/http'
import { AUTH_ENDPOINTS } from '@/features/auth/api/auth.endpoints'
import { PORTAL_AUTH_CLIENT } from '@/core/auth/portal-storage'
import {
  PORTAL_AUTH_SYNC_STORAGE_KEYS,
  clearPortalAuthTokens,
  clearPortalLogoutPending,
  getPortalAccessQrToken,
  getPortalAccessToken,
  isPortalLogoutPending,
  isPortalRememberLogin,
  markPortalLogoutPending,
  setPortalAccessQrToken,
  setPortalAuthTokens,
  setPortalRememberedLogin,
  shouldRefreshPortalAccessToken,
} from '@/core/auth/portal-storage'
import {
  ensureValidPortalAccessToken,
  isPortalAuthRefreshLockHeld,
  redirectToPortalLogin,
  refreshPortalSessionForced,
  startPortalAuthSessionKeeper,
  stopPortalAuthSessionKeeper,
} from '@/core/auth/portal-session-refresh'
import { isHardRefreshFailure } from '@/core/auth/refresh-failure'
import { authClientHeaders } from '@/core/auth/auth-client-context'
import { safeRedirectPath } from '@/shared/utils/safe-redirect-path'

let storageSyncInitialized = false
let fetchMeInFlight: Promise<void> | null = null
let ensureSessionInFlight: Promise<boolean> | null = null

export const useRecruitmentPortalAuthStore = defineStore('recruitment-portal-auth', () => {
  const token = ref<string | null>(getPortalAccessToken())
  const user = ref<User | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  function clearSession(): void {
    fetchMeInFlight = null
    ensureSessionInFlight = null
    stopPortalAuthSessionKeeper()
    clearPortalAuthTokens()
    token.value = null
    user.value = null
  }

  function applyAuthSession(res: AuthTokenResponse, remember: boolean): void {
    setPortalAuthTokens(
      {
        access_token: res.access_token,
        expires_in: res.expires_in,
      },
      remember,
    )
    token.value = res.access_token
  }

  async function syncFromStorage(): Promise<void> {
    if (isPortalLogoutPending()) return

    const stored = getPortalAccessToken()

    if (stored === token.value) {
      if (stored && user.value && !shouldRefreshPortalAccessToken()) return
      if (stored && shouldRefreshPortalAccessToken()) {
        try {
          await ensureValidPortalAccessToken()
          token.value = getPortalAccessToken()
        } catch (error) {
          if (isHardRefreshFailure(error)) {
            clearSession()
            redirectToPortalLogin()
          }
        }
      }
      return
    }

    if (!stored) {
      if (isPortalAuthRefreshLockHeld()) {
        return
      }
      if (token.value || user.value) {
        clearSession()
        redirectToPortalLogin()
      }
      return
    }

    token.value = stored
    try {
      await ensureValidPortalAccessToken()
      token.value = getPortalAccessToken()
    } catch (error) {
      if (isHardRefreshFailure(error)) {
        clearSession()
        redirectToPortalLogin()
        return
      }
    }

    await fetchMe()
    if (!token.value || !user.value) {
      redirectToPortalLogin()
      return
    }
    startPortalAuthSessionKeeper()
  }

  function initStorageSync(): void {
    if (storageSyncInitialized || typeof window === 'undefined') return
    storageSyncInitialized = true

    window.addEventListener('storage', (event) => {
      if (
        event.key === null
        || (PORTAL_AUTH_SYNC_STORAGE_KEYS as readonly string[]).includes(event.key)
      ) {
        void syncFromStorage()
      }
    })

    window.addEventListener('auth:portal-session-cleared', () => {
      clearSession()
    })
  }

  async function login(login: string, password: string, remember = true) {
    const accessQr = getPortalAccessQrToken()
    if (!accessQr) {
      throw new Error('portal access qr required')
    }
    const res = await authApi.login(login, password, remember, PORTAL_AUTH_CLIENT, accessQr)
    if (isTwoFactorChallenge(res)) {
      return res
    }
    applyAuthSession(res, remember)
    setPortalRememberedLogin(login, remember)
    clearPortalLogoutPending()
    user.value = res.user
    startPortalAuthSessionKeeper()
    return res
  }

  async function verifyTwoFactor(challengeId: string, code: string, remember = true) {
    const accessQr = getPortalAccessQrToken()
    if (!accessQr) {
      throw new Error('portal access qr required')
    }
    const res = await authApi.verifyTwoFactor(challengeId, code, PORTAL_AUTH_CLIENT, accessQr)
    applyAuthSession(res, remember)
    clearPortalLogoutPending()
    user.value = res.user
    startPortalAuthSessionKeeper()
    return res
  }

  async function fetchMe(): Promise<void> {
    if (!token.value) return
    if (fetchMeInFlight) return fetchMeInFlight

    fetchMeInFlight = (async () => {
      try {
        await ensureValidPortalAccessToken()
        const res = await authApi.me()
        user.value = res.user
      } catch {
        try {
          await refreshPortalSessionForced()
          token.value = getPortalAccessToken()
          const res = await authApi.me()
          user.value = res.user
        } catch {
          clearSession()
        }
      } finally {
        fetchMeInFlight = null
      }
    })()

    return fetchMeInFlight
  }

  async function tryRestoreFromRefreshCookie(): Promise<boolean> {
    if (isPortalLogoutPending()) return false
    const accessQr = getPortalAccessQrToken()
    if (!accessQr) return false
    try {
      const res = await authApi.refresh(PORTAL_AUTH_CLIENT, accessQr)
      applyAuthSession(res, isPortalRememberLogin())
      clearPortalLogoutPending()
      await fetchMe()
      return !!(token.value && user.value)
    } catch {
      return false
    }
  }

  function bindAccessQr(access: string): void {
    setPortalAccessQrToken(access)
  }

  function hasAccessQr(): boolean {
    return !!getPortalAccessQrToken()
  }

  function adoptQrProfile(row: { id: number; name: string; email?: string | null; is_active?: boolean }): void {
    if (user.value) return
    user.value = {
      id: row.id,
      name: row.name,
      email: row.email ?? null,
      is_active: row.is_active,
    }
  }

  async function ensureSession(options: { allowCookieRefresh?: boolean; skipProfile?: boolean } = {}): Promise<boolean> {
    const { allowCookieRefresh = true, skipProfile = false } = options
    if (isPortalLogoutPending()) return false
    if (ensureSessionInFlight) return ensureSessionInFlight

    ensureSessionInFlight = (async () => {
      if (token.value && (user.value || skipProfile)) {
        startPortalAuthSessionKeeper()
        return true
      }

      await syncFromStorage()

      if (token.value && (user.value || skipProfile)) {
        startPortalAuthSessionKeeper()
        return true
      }

      if (token.value && !skipProfile) {
        await fetchMe()
        if (user.value) {
          startPortalAuthSessionKeeper()
          return true
        }
      }

      if (allowCookieRefresh && (await tryRestoreFromRefreshCookie())) {
        startPortalAuthSessionKeeper()
        return true
      }

      clearSession()
      return false
    })().finally(() => {
      ensureSessionInFlight = null
    })

    return ensureSessionInFlight
  }

  async function redirectIfAuthenticated(
    redirect?: string,
  ): Promise<{ name: string } | { path: string } | null> {
    if (isPortalLogoutPending()) return null

    await syncFromStorage()

    const postAuth = (): { name: string } | { path: string } => {
      const safeRedirect = safeRedirectPath(redirect)
      if (safeRedirect?.startsWith('/recruitment-portal')) {
        return { path: safeRedirect }
      }
      return { name: 'recruitment-portal-staff-intake' }
    }

    if (token.value && user.value) {
      startPortalAuthSessionKeeper()
      return postAuth()
    }

    if (token.value) {
      await fetchMe()
      if (user.value) {
        startPortalAuthSessionKeeper()
        return postAuth()
      }
      clearSession()
    }

    return null
  }

  async function logout(): Promise<void> {
    markPortalLogoutPending()
    stopPortalAuthSessionKeeper()
    const accessToken = getPortalAccessToken()
    try {
      await bareHttp.post(
        AUTH_ENDPOINTS.LOGOUT,
        { auth_client: PORTAL_AUTH_CLIENT },
        {
          headers: {
            ...authClientHeaders(),
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        },
      )
    } catch {
      // Cookie/session may already be invalid
    } finally {
      clearSession()
    }
  }

  return {
    token,
    user,
    isAuthenticated,
    clearSession,
    syncFromStorage,
    initStorageSync,
    login,
    verifyTwoFactor,
    fetchMe,
    adoptQrProfile,
    ensureSession,
    redirectIfAuthenticated,
    logout,
    bindAccessQr,
    hasAccessQr,
  }
})
